import getPopupContent from './PopupContent';

/**
 * Creates a feature object by extracting the fields from the asset object.
 * @param {asset} A facility, rec area, tour entity object that has been converted into an asset.
 */
const getFeature = (asset) => {
    const feature = {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [asset.longitude, asset.latitude]
        },
        properties: {
            description: asset.popupHTML,
            name: asset.name,
            id: asset.entityId,
            featureId: asset.featureId,
            icon: asset.mapIcon
        }
    };

    return feature;
};

/**
 * Converts an array of locations to a GeoJSON and generates a description
 * property to use in the popup.
 */
const locationListToGeoJson = (locationList = []) => {
    const geoJSON = {
        type: 'FeatureCollection',
        features: []
    };

    let asset;
    locationList.forEach((location) => {
        asset = null;

        if (location.facility_longitude && location.facility_latitude) {
            asset = { attributes: [] };
            asset.name = location.facility_name;
            asset.title = location.facility_name;
            asset.mapIcon = location.facility_type_id === '35' ? 'tour_pin' : 'camping_pin';
            asset.entityId = location.facility_id;
            asset.entityType = location.entity_type;
            asset.featureId = `${location.entity_type}_${location.facility_id}`;
            asset.longitude = location.facility_longitude;
            asset.latitude = location.facility_latitude;

            if (location.availability === 'unavailable') {
                asset.attributes.push({ key: 'availability', value: "No availability for your dates" });
            }

            if (location.campsites_count) {
                const campsiteCount = parseInt(location.campsites_count, 10);
                const suffix = campsiteCount > 1 ? 'sites' : 'site';
                asset.attributes.push({ key: 'campsite_count', value: `${location.campsites_count} ${suffix}` });
            }

            if (location.tour_count) {
                const tourCount = parseInt(location.tour_count, 10);
                const suffix = tourCount > 1 ? 'tours' : 'tour';
                asset.attributes.push({ key: 'tour_count', value: `${location.tour_count} ${suffix}` });
            }
        }
        else if (location.recarea_longitude && location.recarea_latitude) {
            asset = { attributes: [] };
            asset.name = location.recarea_name;
            asset.title = location.recarea_name;
            asset.mapIcon = 'recArea_pin';
            asset.entityId = location.recarea_id;
            asset.entityType = 'recarea';
            asset.featureId = `recarea_${location.recarea_id}`;
            asset.longitude = location.recarea_longitude;
            asset.latitude = location.recarea_latitude;
        }

        if (asset !== null) {
            // These are common to both entity types.
            if (location.media && location.media.length > 0) {
                asset.imageUrl = location.media[0].url;
                asset.imageTitle = location.media[0].title;
            }

            if (location.addresses && location.addresses.length > 0) {
                asset.location = `${location.addresses[0].city}, ${location.addresses[0].address_state_code}`;
            }

            // Get the HTML content to use for the popup.
            asset.popupHTML = getPopupContent(asset);

            // Create a feature object.
            const feature = getFeature(asset);

            geoJSON.features.push(feature);
        }
    });

    return geoJSON;
};

/**
 * Converts a campground JSON object with campsites into a geojson format.
 * @param {object} campground object with a campsite list.
 */
const campsiteToGeoJson = (campground = {}) => {
    const geoJSON = {
        type: 'FeatureCollection',
        features: []
    };

    if (campground.campsites && campground.campsites.length > 0) {
        campground.campsites.forEach((location) => {
            // Only create a feature point if it has a lat/lon.
            if (location.longitude && location.latitude) {
                const asset = { attributes: [] };

                asset.name = location.name;
                asset.title = `Site ${location.name} - Loop ${location.loop}`;
                asset.targetUri = `/campsites/${location.campsite_id}`;
                asset.mapIcon = 'campsite_pin';
                asset.entityId = location.campsite_id;
                asset.entityType = 'campsite';
                asset.featureId = `${location.facility_id}_${location.campsite_id}`;
                asset.longitude = location.longitude;
                asset.latitude = location.latitude;

                if (location.type) {
                    asset.attributes.push({ key: 'location_type', value: location.type });
                }

                // Get the HTML content to use for the popup.
                asset.popupHTML = getPopupContent(asset);

                // Create a feature object.
                const feature = getFeature(asset);

                geoJSON.features.push(feature);
            }
        });
    }

    return geoJSON;
};

const getGeoJsonFromResultSet = (resultSet) => {
    if (resultSet && resultSet.campsites !== undefined) {
        return campsiteToGeoJson(resultSet);
    }

    return locationListToGeoJson(resultSet);
};

export { locationListToGeoJson, campsiteToGeoJson, getGeoJsonFromResultSet, getFeature };
